import React, { useState } from "react";
import { DollarSign } from "lucide-react";
import { Modal } from "./Modal";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";

function Field({ label, hint, children }) {
  return (
    <label style={{ display: "block", marginBottom: 10 }}>
      <span style={{ display: "block", fontSize: 11, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--text-3)", marginBottom: 4 }}>
        {label}
      </span>
      {children}
      {hint && <span style={{ fontSize: 11, color: "var(--text-3)", marginTop: 3, display: "block" }}>{hint}</span>}
    </label>
  );
}

/**
 * Flags one or more equipment lines "Awaiting payment" by capturing an
 * invoice reference — the same action the Job Flow Board's InvoiceModal
 * performs for a single card, but usable from Dispatch. Two selection modes:
 *  - `itemIds` (single item, or a bulk-selected batch of items — flat
 *    "Equipment" view or per-item drill-down) → PATCH / POST bulk-payment.
 *  - `jobIds` + `sourceStatus` (whole DN rows selected on the "By Job (DN)"
 *    view's own bulk bar) → resolves to only the lines currently at
 *    `sourceStatus` on those DNs server-side, then applies the same update.
 * Works regardless of the line's operational status (incl. already "closed"
 * — e.g. legacy DNs manually linked into Dispatch) since `settle_item()`
 * safely no-ops on an already-closed item.
 */
export function InvoiceCaptureModal({ itemIds, jobIds, sourceStatus, subtitle, onClose, onDone }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    invoice_number: "", invoice_date: today, invoice_amount: "",
    quote_number: "", purchase_order: "", note: "",
  });
  const [busy, setBusy] = useState(false);
  const valid = !!form.invoice_number.trim();
  const isJobMode = !!jobIds;
  const count = isJobMode ? jobIds.length : itemIds.length;
  const isBulk = isJobMode || itemIds.length > 1;

  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    const payload = {
      payment_status: "awaiting_payment",
      invoice_number: form.invoice_number.trim(),
      invoice_date: form.invoice_date || undefined,
      invoice_amount: form.invoice_amount ? Number(form.invoice_amount) : undefined,
      quote_number: form.quote_number.trim() || undefined,
      purchase_order: form.purchase_order.trim() || undefined,
      note: form.note.trim() || undefined,
    };
    try {
      if (isJobMode) {
        const r = await api.post("/dispatch/jobs/bulk-payment", { job_ids: jobIds, source_status: sourceStatus, ...payload });
        if (r.data.skipped?.length) toast.warning(`${r.data.count} line(s) updated · ${r.data.skipped.length} skipped (see audit log)`);
        else toast.success(`${r.data.count} line(s) → Awaiting payment`);
      } else if (itemIds.length > 1) {
        const r = await api.post("/job-items/bulk-payment", { item_ids: itemIds, ...payload });
        if (r.data.skipped?.length) toast.warning(`${r.data.count} updated · ${r.data.skipped.length} skipped (see audit log)`);
        else toast.success(`${r.data.count} item(s) → Awaiting payment`);
      } else {
        await api.patch(`/job-items/${itemIds[0]}/payment`, payload);
        toast.success("Invoice captured — awaiting payment");
      }
      onDone && onDone();
      onClose && onClose();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} icon={DollarSign}
      title={isJobMode ? `Capture Invoice — ${count} job(s)` : (isBulk ? `Capture Invoice — ${count} items` : "Capture Invoice — Awaiting Payment")}
      subtitle={subtitle}
      footer={
        <>
          <button className="btn ghost" onClick={onClose} disabled={busy} data-testid="invoice-capture-cancel">Cancel</button>
          <button className="btn gold" onClick={submit} disabled={!valid || busy} data-testid="invoice-capture-submit">
            <DollarSign size={13} /> {busy ? "Saving…" : "Capture & flag awaiting payment"}
          </button>
        </>
      }
    >
      <p className="t-mut" style={{ fontSize: 12.5, marginBottom: 12 }}>
        {isJobMode
          ? `This invoice reference will be saved against every "${(sourceStatus || "").replace(/_/g, " ")}" line on the ${count} selected job(s) and each will be flagged Awaiting Payment.`
          : isBulk
          ? "This invoice reference will be saved against ALL selected lines and each will be flagged Awaiting Payment."
          : "Saved against this equipment line and flagged Awaiting Payment."}
      </p>
      <Field label="Sage invoice number *" hint="The invoice reference from Sage / your accounts system.">
        <input className="input mono" value={form.invoice_number}
          onChange={(e) => setForm((f) => ({ ...f, invoice_number: e.target.value }))}
          placeholder="e.g. INV-2026-08412" data-testid="invoice-capture-number" />
      </Field>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <Field label="Invoice date">
          <input type="date" className="input" value={form.invoice_date}
            onChange={(e) => setForm((f) => ({ ...f, invoice_date: e.target.value }))}
            data-testid="invoice-capture-date" />
        </Field>
        <Field label="Amount (R)">
          <input type="number" step="0.01" min="0" className="input" value={form.invoice_amount}
            onChange={(e) => setForm((f) => ({ ...f, invoice_amount: e.target.value }))}
            data-testid="invoice-capture-amount" />
        </Field>
        <Field label="Quote number">
          <input className="input mono" value={form.quote_number}
            onChange={(e) => setForm((f) => ({ ...f, quote_number: e.target.value }))}
            data-testid="invoice-capture-quote" />
        </Field>
        <Field label="PO number">
          <input className="input mono" value={form.purchase_order}
            onChange={(e) => setForm((f) => ({ ...f, purchase_order: e.target.value }))}
            data-testid="invoice-capture-po" />
        </Field>
      </div>
      <Field label="Note">
        <input className="input" value={form.note}
          onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
          data-testid="invoice-capture-note" />
      </Field>
    </Modal>
  );
}
